/**
 * AWARE Health — Main Dashboard (v2 + choropleth)
 *
 * Layout:
 * ┌─────────────────────────────────────────────────────────────────┐
 * │  HEADER: Logo · "Diarrheal Disease Early Warning" · Model badge  │
 * ├──────────────────┬──────────────────────────────────────────────┤
 * │  LEFT SIDEBAR    │  MAP (CartoDB dark tiles, animated markers)   │
 * │  ─ Country cards │  ─────────────────────────────────────────── │
 * │  ─ Province list │  KPI ROW (6 stat cards)                      │
 * │    with risk     │  ─────────────────────────────────────────── │
 * │    badges        │  PLOTLY CHART (risk zones + thresholds)      │
 * └──────────────────┴──────────────────────────────────────────────┘
 *
 * State flow:
 *   Country card click → selectedCountry → useForecast → ProvinceList
 *   Province click → selectedRegionId → KPI cards + ForecastTrendChart
 *   Province hover → hoveredRegionId → synchronized map highlight
 */

import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import ForecastMap from '../components/Map/ForecastMap'
import ForecastTrendChart from '../components/Charts/ForecastTrendChart'
import CountryCard from '../components/UI/CountryCard'
import ProvinceList from '../components/UI/ProvinceList'
import KpiCard from '../components/UI/KpiCard'
import RiskBadge from '../components/UI/RiskBadge'
import AlertBadge from '../components/UI/AlertBadge'
import LoadingSpinner from '../components/UI/LoadingSpinner'
import ThemeToggle from '../components/UI/ThemeToggle'
import ViewModeToggle from '../components/UI/ViewModeToggle'
import LanguageSelector from '../components/UI/LanguageSelector'
import WhatsNew from '../components/UI/WhatsNew'
import { useTheme } from '../contexts/ThemeContext'
import { useViewMode } from '../contexts/ViewModeContext'
import { useForecast } from '../api/forecasts'
import type { ForecastRecord, AlertLevel, RiskCategory } from '../types/forecast'

const DEFAULT_ISSUE_DATE = '2026-W31'

const COUNTRIES = [
  { code: 'VN', name: 'Vietnam', flag: '🇻🇳', issueDate: DEFAULT_ISSUE_DATE },
  { code: 'TW', name: 'Taiwan',  flag: '🇹🇼', issueDate: DEFAULT_ISSUE_DATE },
  { code: 'NP', name: 'Nepal',   flag: '🇳🇵', issueDate: DEFAULT_ISSUE_DATE },
]

/** Format a number as a rate string, e.g. "0.0842" */
function fmtRate(v: number | null | undefined): string {
  if (v == null) return '—'
  return v.toFixed(4)
}

/** Format a percentage, e.g. 0.31 → "31%" */
function fmtPct(v: number | null | undefined): string {
  if (v == null) return '—'
  return `${(v * 100).toFixed(1)}%`
}

function capitalize(s: string) {
  if (!s) return ''
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
}

export default function Dashboard() {
  const { theme } = useTheme()
  const isDark = theme === 'dark'
  const { isDetailed } = useViewMode()
  const { t } = useTranslation()

  const [selectedCountry, setSelectedCountry] = useState<string | null>(null)
  const [selectedIssueDate, setSelectedIssueDate] = useState<string>(DEFAULT_ISSUE_DATE)
  const [selectedRegionId, setSelectedRegionId] = useState<string | null>(null)
  const [hoveredRegionId, setHoveredRegionId] = useState<string | null>(null)

  // ── Data fetch ──────────────────────────────────────────────────────
  const { data: forecastDoc, isPending, isError, error } = useForecast(selectedCountry, selectedIssueDate)

  // ── Province records for selected region ────────────────────────────
  const regionRecords = useMemo((): ForecastRecord[] => {
    if (!forecastDoc || !selectedRegionId) return []
    return forecastDoc.forecasts
      .filter((r) => r.region_id === selectedRegionId)
      .sort((a, b) => a.horizon - b.horizon)
  }, [forecastDoc, selectedRegionId])

  // ── First forecast record (horizon=1) for KPI cards ────────────────
  const kpiRecord = regionRecords[0] ?? null

  // ── forecastByRegionName: region_name → {regionId, record} for choropleth ──
  const forecastByRegionName = useMemo(() => {
    const map = new Map<string, { regionId: string; record: ForecastRecord }>()
    if (!forecastDoc) return map
    for (const r of forecastDoc.forecasts) {
      if (r.horizon !== 1) continue
      if (!map.has(r.region_name)) {
        map.set(r.region_name, { regionId: r.region_id, record: r })
      }
    }
    return map
  }, [forecastDoc])

  // ── Alert / risk summary across all provinces (for country card pills) ──
  const alertCounts = useMemo(() => {
    if (!forecastDoc) return { watch: 0, warning: 0, emergency: 0 }
    // Use only horizon=1 records per region
    const seen = new Set<string>()
    let watch = 0, warning = 0, emergency = 0
    for (const r of forecastDoc.forecasts) {
      if (r.horizon !== 1) continue
      if (seen.has(r.region_id)) continue
      seen.add(r.region_id)
      if (r.alert_level === 'Emergency') emergency++
      else if (r.alert_level === 'Warning') warning++
      else if (r.alert_level === 'Watch') watch++
    }
    return { watch, warning, emergency }
  }, [forecastDoc])

  // ── Risk/alert by country code (for map markers) ────────────────────
  const riskByCountry: Record<string, string> = useMemo(() => {
    if (!forecastDoc || !selectedCountry) return {}
    const order: Record<RiskCategory, number> = { high: 2, medium: 1, low: 0 }
    let best: RiskCategory = 'low'
    for (const r of forecastDoc.forecasts) {
      if (r.horizon !== 1) continue
      if ((order[r.risk_category] ?? 0) > (order[best] ?? 0)) best = r.risk_category
    }
    return { [selectedCountry]: best }
  }, [forecastDoc, selectedCountry])

  const alertByCountry: Record<string, string> = useMemo(() => {
    if (!forecastDoc || !selectedCountry) return {}
    const alertOrder: Record<AlertLevel, number> = { Emergency: 3, Warning: 2, Watch: 1, None: 0 }
    let best: AlertLevel = 'None'
    for (const r of forecastDoc.forecasts) {
      if (r.horizon !== 1) continue
      if ((alertOrder[r.alert_level] ?? 0) > (alertOrder[best] ?? 0)) best = r.alert_level
    }
    return { [selectedCountry]: best }
  }, [forecastDoc, selectedCountry])

  // ── Handlers ─────────────────────────────────────────────────────────
  function handleCountrySelect(code: string, issueDate: string) {
    if (selectedCountry === code) return
    setSelectedCountry(code)
    setSelectedIssueDate(issueDate)
    setSelectedRegionId(null)
    setHoveredRegionId(null)
  }

  function handleProvinceSelect(regionId: string) {
    setSelectedRegionId(regionId)
  }

  function handleProvinceHover(regionId: string | null) {
    setHoveredRegionId(regionId)
  }

  // ── Derived display values ───────────────────────────────────────────
  const selectedRegionName = kpiRecord?.region_name ?? ''
  const countryName = COUNTRIES.find((c) => c.code === selectedCountry)?.name ?? ''

  // Translated name for display; `countryName` (English) is still used for metadata lookups
  //const countryDisplayName = selectedCountry ? t(`country.${selectedCountry}`, countryName) : ''

  // Colors (contrast-checked against each theme's background)
  const bgMain = isDark ? '#030712' : '#f8fafc'
  const headerBg = isDark ? 'rgba(5,10,20,0.8)' : 'rgba(255,255,255,0.9)'
  const borderCol = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.07)'
  const textPrimary = isDark ? '#f1f5f9' : '#0f172a'
  const textSecondary = isDark ? '#94a3b8' : '#64748b'
  const textMuted = isDark ? '#64748b' : '#94a3b8'
  const sidebarBg = isDark ? 'rgba(5,10,20,0.5)' : 'rgba(255,255,255,0.7)'
  const dividerCol = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'
  const metaText = isDark ? '#64748b' : '#94a3b8'
  const emptyCardBg = isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)'
  const tagBg = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'
  const tagBorder = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        background: bgMain,
        overflow: 'hidden',
      }}
    >

      {/* ── HEADER ─────────────────────────────────────────────────────── */}
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 20px',
          height: 56,
          borderBottom: `1px solid ${borderCol}`,
          background: headerBg,
          backdropFilter: 'blur(12px)',
          flexShrink: 0,
          position: 'relative',
          zIndex: 9999,
        }}
      >
        {/* Brand */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 8,
              background: 'linear-gradient(135deg, #0d9488, #2563eb)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 13,
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.5px',
              flexShrink: 0,
            }}
          >
            AW
          </div>
          <div>
            <h1
              style={{
                margin: 0,
                fontSize: 15,
                fontWeight: 700,
                color: textPrimary,
                lineHeight: 1.2,
              }}
            >
              {t('app.title', 'AWARE Health')}
            </h1>
            <p
              style={{
                margin: 0,
                fontSize: 10,
                color: textSecondary,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
            >
              {t('app.subtitle', 'Diarrheal Disease Early Warning System')}
            </p>
          </div>
        </div>

        {/* Right side: meta badges and controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 10px',
              borderRadius: 20,
              background: 'rgba(16,185,129,0.08)',
              border: '1px solid rgba(16,185,129,0.2)',
              fontSize: 11,
              color: '#10b981',
              fontWeight: 600,
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: '#10b981',
                boxShadow: '0 0 6px #10b981',
                display: 'inline-block',
              }}
            />
            {t('header.live', 'Live')}
          </div>

          {isDetailed && (
            <>
              <div
                style={{
                  padding: '4px 10px',
                  borderRadius: 20,
                  background: isDark ? 'rgba(37,99,235,0.1)' : 'rgba(37,99,235,0.06)',
                  border: `1px solid ${isDark ? 'rgba(37,99,235,0.2)' : 'rgba(37,99,235,0.18)'}`,
                  fontSize: 10,
                  color: isDark ? '#93c5fd' : '#1d4ed8',
                  fontFamily: 'JetBrains Mono, monospace',
                  letterSpacing: '0.02em',
                }}
              >
                dssm-0.3.0-seascal
              </div>

              <div
                style={{
                  padding: '4px 10px',
                  borderRadius: 20,
                  background: isDark ? 'rgba(20,184,166,0.08)' : 'rgba(13,148,136,0.06)',
                  border: `1px solid ${isDark ? 'rgba(20,184,166,0.15)' : 'rgba(13,148,136,0.2)'}`,
                  fontSize: 10,
                  color: isDark ? '#5eead4' : '#0f766e',
                }}
              >
                Seasonal Adaptive Conformal
              </div>
            </>
          )}

          <div style={{ width: 1, height: 24, background: dividerCol, margin: '0 4px' }} />

          <WhatsNew />
          <LanguageSelector />
          <ThemeToggle />
          <ViewModeToggle />
        </div>
      </header>

      {/* ── MAIN CONTENT ──────────────────────────────────────────────── */}
      <main style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* ── LEFT SIDEBAR ─────────────────────────────────────────────── */}
        <aside
          style={{
            width: 240,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            borderRight: `1px solid ${borderCol}`,
            background: sidebarBg,
            overflow: 'hidden',
          }}
        >
          {/* Country selector section */}
          <div style={{ padding: '12px 12px 8px', flexShrink: 0 }}>
            <div
              style={{
                fontSize: 9,
                color: textMuted,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                fontWeight: 600,
                marginBottom: 8,
                paddingLeft: 4,
              }}
            >
              {t('sidebar.countries', 'Countries')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {COUNTRIES.map((c) => (
                <CountryCard
                  key={c.code}
                  code={c.code}
                  name={t(`country.${c.code}`, c.name)}
                  flag={c.flag}
                  isSelected={selectedCountry === c.code}
                  isLoading={selectedCountry === c.code && isPending}
                  alertCounts={selectedCountry === c.code ? alertCounts : undefined}
                  onClick={() => handleCountrySelect(c.code, c.issueDate)}
                />
              ))}
            </div>
          </div>

          <div
            style={{
              height: 1,
              background: dividerCol,
              margin: '4px 12px',
              flexShrink: 0,
            }}
          />

          {/* Province list section */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div
              style={{
                fontSize: 9,
                color: textMuted,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                fontWeight: 600,
                padding: '8px 16px 6px',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <span>
                {selectedCountry === 'TW' ? t('sidebar.counties', 'Counties') : t('sidebar.provinces', 'Provinces')}
              </span>
              {forecastDoc && (
                <span style={{ color: metaText, fontSize: 9 }}>
                  {t('sidebar.sortedBySeverity', 'sorted by severity')}
                </span>
              )}
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '0 8px 12px' }}>
              {!selectedCountry && (
                <div
                  style={{
                    padding: '20px 12px',
                    textAlign: 'center',
                    color: metaText,
                    fontSize: 11,
                  }}
                >
                  {t('sidebar.selectCountry', 'Select a country above')}
                </div>
              )}
              {selectedCountry && isPending && (
                <div style={{ padding: '24px 0' }}>
                  <LoadingSpinner label={t('loading.provinces', 'Loading provinces...')} size="sm" />
                </div>
              )}
              {selectedCountry && isError && (
                <div
                  style={{
                    padding: 12,
                    fontSize: 11,
                    color: '#ef4444',
                    textAlign: 'center',
                  }}
                >
                  {t('error.failedToLoad', 'Failed to load data')}
                  <br />
                  <span style={{ color: textSecondary, fontSize: 10 }}>
                    {error instanceof Error ? error.message : t('error.unknown', 'Unknown error')}
                  </span>
                </div>
              )}
              {forecastDoc && (
                <ProvinceList
                  records={forecastDoc.forecasts}
                  selectedRegionId={selectedRegionId}
                  hoveredRegionId={hoveredRegionId}
                  onSelect={handleProvinceSelect}
                  onHover={handleProvinceHover}
                />
              )}
            </div>
          </div>

          {/* Footer */}
          {forecastDoc && (
            <div
              style={{
                padding: '8px 12px',
                borderTop: `1px solid ${dividerCol}`,
                flexShrink: 0,
              }}
            >
              <div style={{ fontSize: 9, color: metaText, lineHeight: 1.5 }}>
                <div>{t('sidebar.generated', 'Generated:')} {new Date(forecastDoc.metadata.generated_at).toLocaleDateString()}</div>
                {forecastDoc.metadata.calibration_coverage_achieved?.[countryName] && (
                  <div>
                    {t('sidebar.pi95Coverage', 'PI95 coverage:')}{' '}
                    {(forecastDoc.metadata.calibration_coverage_achieved[countryName].PI95_per_region * 100).toFixed(1)}%
                  </div>
                )}
              </div>
            </div>
          )}
        </aside>

        {/* ── RIGHT CONTENT AREA ──────────────────────────────────────── */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minWidth: 0,
          }}
        >
          {/* ── MAP ─────────────────────────────────────────────────── */}
          <div
            style={{
              height: 300,
              flexShrink: 0,
              padding: '10px 10px 0',
              position: 'relative',
            }}
          >
            <ForecastMap
              selectedCountry={selectedCountry}
              onRegionSelect={handleCountrySelect}
              onProvinceClick={handleProvinceSelect}
              onProvinceHover={handleProvinceHover}
              selectedRegionId={selectedRegionId}
              hoveredRegionId={hoveredRegionId}
              riskByCountry={riskByCountry}
              alertByCountry={alertByCountry}
              forecastByRegionName={forecastDoc ? forecastByRegionName : undefined}
            />
            {/* Map legend */}
            {selectedCountry && forecastDoc && (
              <div
                style={{
                  position: 'absolute',
                  bottom: 8,
                  right: 18,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '5px 10px',
                  borderRadius: 8,
                  background: 'rgba(5,10,20,0.85)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  backdropFilter: 'blur(8px)',
                  fontSize: 10,
                  zIndex: 1000,
                }}
              >
                <span style={{ color: textMuted, letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: 9 }}>
                  {t('map.risk', 'Risk')}
                </span>
                {[
                  { label: t('map.low', 'Low'),    color: '#10b981' },
                  { label: t('map.medium', 'Medium'), color: '#f59e0b' },
                  { label: t('map.high', 'High'),   color: '#ef4444' },
                  { label: t('map.noData', 'No data'), color: 'rgba(255,255,255,0.15)' },
                ].map((item) => (
                  <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: item.color, display: 'inline-block', flexShrink: 0 }} />
                    <span style={{ color: '#64748b' }}>{item.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── KPI CARDS ROW ──────────────────────────────────────── */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isDetailed ? 'repeat(6, 1fr)' : 'repeat(3, 1fr)',
              gap: 8,
              padding: '8px 10px',
              flexShrink: 0,
            }}
          >
            {kpiRecord ? (
              isDetailed ? (
                <>
                  <KpiCard
                    icon="📈"
                    label={t('kpiDetailed.calibratedRate', 'Calibrated Rate')}
                    value={fmtRate(kpiRecord.forecast_calibrated_median)}
                    sub={t('kpiDetailed.calibratedRateSub', 'per 100k (PI50 midpoint)')}
                    accent="#14b8a6"
                  />
                  <KpiCard
                    icon="⚠️"
                    label={t('kpiDetailed.riskCategory', 'Risk Category')}
                    value={<RiskBadge risk={kpiRecord.risk_category} size="md" />}
                    sub={`${t('kpiDetailed.horizon', 'Horizon')} ${kpiRecord.horizon} ${kpiRecord.horizon_unit}`}
                    accent={
                      kpiRecord.risk_category === 'high' ? '#ef4444' :
                      kpiRecord.risk_category === 'medium' ? '#f59e0b' : '#10b981'
                    }
                  />
                  <KpiCard
                    icon="🔔"
                    label={t('kpiDetailed.alertLevel', 'Alert Level')}
                    value={<AlertBadge level={kpiRecord.alert_level} size="md" />}
                    sub={`${t('kpiDetailed.issued', 'Issued')} ${kpiRecord.issue_date}`}
                    accent={
                      kpiRecord.alert_level === 'Emergency' ? '#a855f7' :
                      kpiRecord.alert_level === 'Warning'   ? '#ef4444' :
                      kpiRecord.alert_level === 'Watch'     ? '#f59e0b' : '#475569'
                    }
                  />
                  <KpiCard
                    icon="📊"
                    label={t('kpiDetailed.pi95Range', 'PI95 Range')}
                    value={`${fmtRate(kpiRecord.pi95_low)}–${fmtRate(kpiRecord.pi95_high)}`}
                    sub={t('kpiDetailed.pi95RangeSub', '95% prediction interval')}
                    accent="#60a5fa"
                  />
                  <KpiCard
                    icon="🎯"
                    label={t('kpiDetailed.probHighRisk', 'Prob. High Risk')}
                    value={fmtPct(kpiRecord.prob_exceed_high)}
                    sub={`${t('kpiDetailed.med', 'Med')}: ${fmtPct(kpiRecord.prob_medium)} · ${t('kpiDetailed.low', 'Low')}: ${fmtPct(kpiRecord.prob_low)}`}
                    accent={kpiRecord.prob_exceed_high > 0.4 ? '#ef4444' : kpiRecord.prob_exceed_high > 0.2 ? '#f59e0b' : '#10b981'}
                  />
                  <KpiCard
                    icon="↗"
                    label={t('kpiDetailed.trendSignal', 'Trend Signal')}
                    value={kpiRecord.trend_signal ?? t('kpiDetailed.stable', 'Stable')}
                    sub={`${t('kpiDetailed.target', 'Target')}: ${kpiRecord.target_start}`}
                    accent="#a855f7"
                  />
                </>
              ) : (
                <>
                  <KpiCard
                    icon="⚠️"
                    label={t('kpiSimple.riskLevel', 'Risk Level')}
                    value={<RiskBadge risk={kpiRecord.risk_category} size="md" />}
                    sub={t('kpiSimple.riskSub')}
                    accent={
                      kpiRecord.risk_category === 'high' ? '#ef4444' :
                      kpiRecord.risk_category === 'medium' ? '#f59e0b' : '#10b981'
                    }
                  />
                  <KpiCard
                    icon="🔔"
                    label={t('kpiSimple.alertStatus', 'Alert Status')}
                    value={<AlertBadge level={kpiRecord.alert_level} size="md" />}
                    sub={t(`kpiSimple.alertSub${capitalize(kpiRecord.alert_level)}`)}
                    accent={
                      kpiRecord.alert_level === 'Emergency' ? '#a855f7' :
                      kpiRecord.alert_level === 'Warning'   ? '#ef4444' :
                      kpiRecord.alert_level === 'Watch'     ? '#f59e0b' : '#475569'
                    }
                  />
                  <KpiCard
                    icon="📅"
                    label={t('kpiSimple.forecastPeriod', 'Forecast Period')}
                    value={`${kpiRecord.target_start} ${t('kpiSimple.periodLabel')} ${kpiRecord.target_end}`}
                    sub={t('kpiSimple.periodSub')}
                    accent="#60a5fa"
                  />
                </>
              )
            ) : (
              Array.from({ length: isDetailed ? 6 : 3 }).map((_, i) => (
                <div
                  key={i}
                  className="glass-card rounded-xl p-4"
                  style={{ minHeight: 80, display: 'flex', flexDirection: 'column', gap: 8, background: emptyCardBg }}
                >
                  <div className="skeleton" style={{ height: 10, width: '60%' }} />
                  <div className="skeleton" style={{ height: 22, width: '80%' }} />
                  <div className="skeleton" style={{ height: 9, width: '70%' }} />
                </div>
              ))
            )}
          </div>

          {/* ── CHART AREA ─────────────────────────────────────────── */}
          <div
            style={{
              flex: 1,
              padding: '0 10px 10px',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div
              className="glass-card rounded-xl"
              style={{
                flex: 1,
                padding: 0,
                overflow: 'hidden',
                position: 'relative',
                minHeight: 0,
              }}
            >
              {!selectedCountry && (
                <div
                  style={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 12,
                    color: metaText,
                  }}
                >
                  <div style={{ fontSize: 48, filter: 'grayscale(0.3)' }}>🌏</div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: textMuted }}>
                    {t('chart.selectCountry', 'Select a country to begin')}
                  </div>
                  <div style={{ fontSize: 12, color: metaText }}>
                    {t('chart.countriesList', 'Vietnam · Taiwan · Nepal')}
                  </div>
                </div>
              )}

              {selectedCountry && isPending && (
                <div
                  style={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <LoadingSpinner label={t('loading.forecast', 'Loading forecast data...')} size="lg" />
                </div>
              )}

              {selectedCountry && !isPending && !isError && forecastDoc && !selectedRegionId && (
                <div
                  className="animate-fade-in-up"
                  style={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 12,
                    color: metaText,
                  }}
                >
                  <div style={{ fontSize: 40 }}>📍</div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: textMuted }}>
                    {t('chart.selectProvince', 'Select a province from the map or sidebar')}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: metaText,
                      display: 'flex',
                      gap: 6,
                      flexWrap: 'wrap',
                      justifyContent: 'center',
                      maxWidth: 360,
                    }}
                  >
                    {forecastDoc.forecasts
                      .filter((r) => r.horizon === 1)
                      .slice(0, 5)
                      .map((r) => (
                        <span
                          key={r.region_id}
                          style={{
                            padding: '2px 8px',
                            borderRadius: 10,
                            background: tagBg,
                            border: `1px solid ${tagBorder}`,
                          }}
                        >
                          {r.region_name}
                        </span>
                      ))}
                    {forecastDoc.forecasts.filter((r) => r.horizon === 1).length > 5 && (
                      <span style={{ color: textMuted }}>
                        +{forecastDoc.forecasts.filter((r) => r.horizon === 1).length - 5} {t('chart.more', 'more')}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {selectedRegionId && regionRecords.length > 0 && (
                <div
                  className="animate-fade-in-up"
                  style={{ width: '100%', height: '100%', padding: '6px 4px 0' }}
                >
                  <ForecastTrendChart
                    records={regionRecords}
                    regionName={selectedRegionName}
                    country={countryName}
                  />
                </div>
              )}

              {selectedCountry && isError && (
                <div
                  style={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                  }}
                >
                  <div style={{ fontSize: 32 }}>⚠️</div>
                  <div style={{ fontSize: 14, color: '#ef4444', fontWeight: 500 }}>
                    {t('error.failedToLoad', 'Failed to load forecast data')}
                  </div>
                  <div style={{ fontSize: 12, color: textSecondary }}>
                    {error instanceof Error ? error.message : t('error.unknown', 'Unknown error')}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
